import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const categoryIcons: Record<string, string> = {
  "Shoes": "ri-footprint-line",
  "Hoodies/Sweaters": "ri-t-shirt-line",
  "T-Shirts": "ri-shirt-line",
  "Jackets": "ri-shirt-line",
  "Pants/Shorts": "ri-layout-bottom-line",
  "Headwear": "ri-cake-2-line",
  "Sets": "ri-stack-line",
  "Underwear/Underpants": "ri-checkbox-blank-circle-line",
  "Jersey": "ri-basketball-line",
  "Accessories": "ri-vip-diamond-line",
  "Bags": "ri-shopping-bag-line",
  "Belts": "ri-link",
  "Perfumes": "ri-drop-line",
  "OtherStuff": "ri-more-line",
};

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 md:h-[72px]">
            <Link
              to="/"
              className="font-heading text-xl md:text-2xl font-bold whitespace-nowrap text-foreground-950 relative z-10"
            >
              SugarGoo
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-sm font-semibold text-foreground-950 whitespace-nowrap hover:text-primary-600 transition-colors"
              >
                Home
              </Link>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 whitespace-nowrap cursor-pointer transition-colors"
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                  aria-label="Open categories menu"
                >
                  Categories
                  <i
                    className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-background-50 rounded-xl border border-background-200/80 py-2 shadow-lg z-[60]">
                    <div className="grid grid-cols-1 max-h-80 overflow-y-auto">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100">
                            <i className={`${categoryIcons[cat.name] || "ri-price-tag-3-line"} text-xs text-foreground-500`}></i>
                          </span>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/blog/sugargoo-spreadsheet-guide"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors"
              >
                Shopping Guide
              </Link>

              <Link
                to="/blog/best-sugargoo-spreadsheet-shoes"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors"
              >
                Blog
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Main Store
              </a>
            </div>

            <button
              onClick={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen);
                setIsCategoriesOpen(false);
              }}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer relative z-10"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <i className={`text-lg ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-16 z-40 bg-background-50 md:hidden overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-home-4-line text-primary-600"></i>
              </span>
              Home
            </Link>

            <button
              onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
              className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              aria-expanded={isCategoriesOpen}
              aria-label="Toggle categories"
            >
              <span className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-grid-line text-accent-600"></i>
                </span>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${isCategoriesOpen ? "rotate-180" : ""}`}></i>
            </button>

            {isCategoriesOpen && (
              <div className="ml-4 border-l-2 border-background-200 pl-4 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded bg-background-100">
                      <i className={`${categoryIcons[cat.name] || "ri-price-tag-3-line"} text-xs text-foreground-400`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/blog/sugargoo-spreadsheet-guide"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100">
                <i className="ri-book-open-line text-secondary-600"></i>
              </span>
              Shopping Guide
            </Link>

            <Link
              to="/blog/best-sugargoo-spreadsheet-shoes"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                <i className="ri-article-line text-foreground-500"></i>
              </span>
              Blog Articles
            </Link>

            <div className="pt-3 mt-3 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-external-link-line"></i>
                Main Store
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}